<template>
  <div>
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>
    <div class="p-6 pb-0">
      <div class="flex items-center justify-between mb-4">
        <label class="block text-sm font-medium mb-2">{{ $t('gm.list_bills_of_lading') }}</label>
      </div>
      
      <PageServerTable
        :headers="headers" 
        :items="items" 
        :items-per-page.sync="ipp" 
        :items-per-page-options="ippo" 
        :sort-by="sortby" 
        :items-length="count" 
        :reload-fn="reload"
        :mobile-filter-exclusions="['select']"

        show-select
        v-model.selected="selected"
        :item-value="'PurchaseDocId'"

        :page="page"
        show-current-page
        >
        <template #item.OrderDate="{ item }">
            <span>
              {{ formatDate(item.OrderDate) }}
            </span>
        </template>
        <template #item.Supplier="{ item }">
            <span>
              {{ item.CompanyName ?? item.Supplier }}
            </span>
        </template>
        <template #item.actions="{ item }">
          <button
            @click="confirmDelete(item)"
            class="inline-flex items-center gap-1 px-2 py-1 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold rounded shadow-sm transition-all active:scale-95"
            title="Elimina bolla"
          >
            <v-icon name="fa-trash" scale="0.85" />
          </button>
        </template>
      </PageServerTable>
    </div>
    <div class="pb-24"></div>
  </div>

  <ConfirmationModal :show="showDeleteModal" @close="showDeleteModal = false">
    <template #title>
      Elimina bolla di carico
    </template>
    <template #content>
      <div>
        Confermi l'eliminazione della bolla <strong>{{ bollaToDelete?.DocNo }}</strong>?<br>
        <span class="text-sm text-gray-500">L'operazione non è reversibile.</span>
      </div>
    </template>
    <template #footer>
      <SecondaryButton @click="showDeleteModal = false" :disabled="isDeleting">
        {{ $t('actions.cancel') }}
      </SecondaryButton>
      <button
        class="ms-3 inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded transition-all disabled:opacity-50"
        :disabled="isDeleting"
        @click="deleteBolla"
      >
        {{ isDeleting ? 'Eliminazione...' : 'Elimina' }}
      </button>
    </template>
  </ConfirmationModal>

  <div class="fixed left-0 right-0 bottom-0 flex justify-center items-center pointer-events-none">
    <div class="flex gap-2 pointer-events-auto bg-white w-full px-4 py-3 justify-center">
      <button v-if="selected.length == 0" class="text-white bg-blue-400 cursor-not-allowed font-medium rounded-lg text-sm px-5 py-2.5 text-center">{{ $t('core.load') }}</button>
      <button v-if="selected.length > 0" class="text-white bg-blue-400 hover:bg-blue-500 font-medium rounded-lg text-sm px-5 py-2.5 text-center" @click="loadOrdersData">{{ $t('core.load') }} {{ selected.map(x=>x.DocNo).join(", ") }}</button>
    </div>
  </div>

  <ToastNotification
    :show="toast.show"
    :message="toast.message"
    :type="toast.type"
    @update:show="toast.show = $event"
  />
</template>

<script>
import { Link, router, useForm } from '@inertiajs/vue3';
import axios from 'axios';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Pagination from '@/Components/Pagination.vue';
import PageServerTable from '@/Components/PageServerTable.vue';
import ConfirmationModal from '@/Components/ConfirmationModal.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import ToastNotification from '@/Components/ToastNotification.vue';

export default {
  props: ['items', 'count', 'ippo'],
  components: {
    Link,
    BreadCrumbs,
    Pagination,
    PageServerTable,
    ConfirmationModal,
    SecondaryButton,
    ToastNotification,
  },
  data() {
    return {
      search: this.filters?.search || "",
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: this.$t('modules.gestione-magazzino'), href: route('modules.home', 'gestione-magazzino') },
        { name: this.$t('modules.conferma_bolle_carico'), href: route('modules.gm.confermabollecarico') }
      ],
      selected: [],
      headers: [],
      sortby: [],
      ipp: 10,
      page: 1,
      showDeleteModal: false,
      bollaToDelete: null,
      isDeleting: false,
      toast: {
        show: false,
        message: '',
        type: 'success',
      },
    };
  },
  methods: {
    reload(sortby = undefined, itemsperpage = undefined, page = undefined, afterdeletion = false) {
        if (sortby) this.sortby = sortby;
        if (itemsperpage) this.ipp = itemsperpage;
        if (page) this.page = page;

        router.post(window.location.href, { headers: this.headers, sortby: this.sortby, itemsperpage: this.ipp, page: this.page}, { only: ['items', 'count']});
    },

    loadOrdersData() {
      router.post(route('modules.gm.bolleDettagli'), { ids: this.selected.map(x=>x.PurchaseDocId)}, { preserveUrl: true});
    },

    confirmDelete(item) {
      this.bollaToDelete = item;
      this.showDeleteModal = true;
    },

    async deleteBolla() {
      if (!this.bollaToDelete) return;
      this.isDeleting = true;

      try {
        await axios.delete(route('modules.gm.eliminaBolla', { purchaseDocId: this.bollaToDelete.PurchaseDocId }));
        this.showDeleteModal = false;
        this.bollaToDelete = null;
        this.showToast('Bolla eliminata con successo', 'success');
        this.reload();
      } catch (error) {
        const errors = error.response?.data?.errors;
        const msg = errors?.length ? errors.join('; ') : 'Errore durante l\'eliminazione';
        this.showToast(msg, 'error');
      } finally {
        this.isDeleting = false;
      }
    },

    showToast(message, type = 'success') {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;
    },
  },
  mounted() {
    this.headers = this.$attrs.headers;
    this.ipp = this.$attrs.ipp;
    this.page = this.$attrs.page;
    this.sortby = this.$attrs.sortby;
  }
};
</script>
